import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ProtectedRoute } from '../components/auth/index.js';
import {
  CcUsagePanel,
  CcUsageRankingsPanel,
  tokenAmount,
} from '../components/features/cc-usage/index.js';
import { CcEventsPanel } from '../components/features/cc-events/index.js';
import { formatRepositoryName } from '../components/features/cc-usage/formatters.js';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Picker } from '@cortex/web-design-system/components/ui/Picker';
import { MultiPicker } from '@cortex/web-design-system/components/ui/MultiPicker';
import { MemberPicker } from '../components/ui/MemberPicker/index.js';
import { useAuth } from '../hooks/index.js';
import {
  useCcUsageByRepository,
  useCcUsageByTeam,
  useCcUsageByUser,
  useCcUsageMembers,
  useCcUsageSummary,
  useCcUsageTeams,
  useCcUsageTimeseries,
  useCcUsageTimeseriesByGroup,
} from '../hooks/useCcUsage.js';
import {
  useCcEventsByName,
  useCcEventsByProject,
  useCcEventsByUser,
  useCcEventsSummary,
  useCcEventsTimeseries,
} from '../hooks/useCcEvents.js';
import {
  parseCcUsageSearch,
  resolveCcUsageSearch,
  stripCcUsageSearchDefaults,
  type CcUsageSearch,
} from './-cc-usage-search.js';
import type { CcEventsEventType } from '../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Claude Code 利用量ダッシュボードページ。全体 / チーム / 個人 のスコープと期間で集計を切り替えて表示する。全体・チームスコープでは bucket × team / nickname の積み上げグラフも表示する。state は URL search params に sparse 同期し、リロード後も復元される
 * @graph-connects useAuth [calls] ログインユーザー取得
 * @graph-connects useCcUsageTeams [calls] チームスコープの選択肢取得
 * @graph-connects useCcUsageMembers [calls] 集計対象フィルタの nickname 一覧取得
 * @graph-connects useCcUsageSummary [calls] dashboard サマリ取得
 * @graph-connects useCcUsageTimeseries [calls] dashboard 時系列取得
 * @graph-connects useCcUsageTimeseriesByGroup [calls] dashboard 積み上げ時系列取得
 * @graph-connects useCcUsageByUser [calls] dashboard user 別 breakdown 取得
 * @graph-connects useCcUsageByRepository [calls] dashboard repository 別 breakdown 取得
 * @graph-connects useCcUsageByTeam [calls] dashboard team 別 breakdown 取得
 * @graph-connects CcUsagePanel [calls] dashboard 描画
 * @graph-connects MemberPicker [calls] 個人スコープのメンバー選択 UI
 * @graph-connects MultiPicker [calls] チーム・メンバー除外フィルタ UI
 * @graph-connects CcUsageRankingsPanel [calls] dashboard ランキング描画
 * @graph-connects useCcEventsSummary [calls] 実行イベントサマリ取得
 * @graph-connects useCcEventsTimeseries [calls] 実行イベント時系列取得
 * @graph-connects useCcEventsByName [calls] 実行イベント event_name 別ランキング取得
 * @graph-connects useCcEventsByUser [calls] 実行イベント user 別 breakdown 取得
 * @graph-connects useCcEventsByProject [calls] 実行イベント Backlog チケット別 breakdown 取得
 * @graph-connects CcEventsPanel [calls] 実行イベントセクション描画
 */
export const Route = createFileRoute('/cc-usage')({
  component: CcUsageRoute,
  validateSearch: parseCcUsageSearch,
});

/**
 * ルートガード。内部メンバーのみアクセス可能（サイドバー表示と同一の SSOT で判定）。
 * @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする
 */
function CcUsageRoute() {
  return (
    <ProtectedRoute pageKey="ccUsage">
      <CcUsagePage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function CcUsagePage() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const search = Route.useSearch();
  const resolved = useMemo(() => resolveCcUsageSearch(search), [search]);
  const navigate = useNavigate({ from: Route.fullPath });

  const updateSearch = useCallback(
    (patch: Partial<CcUsageSearch>) => {
      void navigate({
        search: (prev) => stripCcUsageSearchDefaults({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );

  const {
    scope,
    range,
    granularity,
    metric,
    team: selectedTeam,
    email: selectedEmail,
    excludeTeams: excludedTeams,
    excludeNicknames: excludedNicknames,
  } = resolved;

  const { data: teamsData } = useCcUsageTeams({ range });
  const { data: membersData } = useCcUsageMembers({ range });

  const teamOptions = useMemo(() => {
    return (teamsData?.data ?? [])
      .map((team) => ({ value: team.teamName, label: team.teamName }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [teamsData]);

  const memberExcludeOptions = useMemo(() => {
    return (membersData?.data ?? []).map((member) => ({
      value: member.nicknameKey,
      label: member.nickname,
      description: member.teamName ?? undefined,
    }));
  }, [membersData]);

  const periodOptions = [
    { value: '7d', label: t('time.last7Days') },
    { value: '14d', label: t('time.last14Days') },
    { value: '30d', label: t('time.last30Days') },
    { value: '90d', label: t('time.last90Days') },
    { value: '180d', label: t('time.last180Days') },
    { value: '365d', label: t('time.last1Year') },
  ];

  const scopeOptions = [
    { value: 'overall', label: t('ccUsage.scope.overall') },
    { value: 'team', label: t('ccUsage.scope.team') },
    { value: 'member', label: t('ccUsage.scope.member') },
  ];

  const granularityOptions = [
    { value: 'day', label: t('ccUsage.granularity.day') },
    { value: 'week', label: t('ccUsage.granularity.week') },
    { value: 'month', label: t('ccUsage.granularity.month') },
  ];

  const metricOptions = [
    { value: 'total', label: t('ccUsage.tokenMetric.total') },
    { value: 'inputOutput', label: t('ccUsage.tokenMetric.inputOutput') },
  ];

  useEffect(() => {
    if (scope === 'team' && !selectedTeam && teamOptions[0]) {
      updateSearch({ team: teamOptions[0].value });
    }
  }, [scope, selectedTeam, teamOptions, updateSearch]);

  useEffect(() => {
    if (scope === 'member' && !selectedEmail && user?.email) {
      updateSearch({ email: user.email });
    }
  }, [scope, selectedEmail, user?.email, updateSearch]);

  const queryParams = useMemo(() => {
    const exclude = { excludeTeams: excludedTeams, excludeNicknames: excludedNicknames };
    if (scope === 'team') {
      return { scope, range, team: selectedTeam ?? undefined, ...exclude };
    }
    if (scope === 'member') {
      return { scope, range, email: selectedEmail ?? undefined, ...exclude };
    }
    return { scope: 'overall' as const, range, ...exclude };
  }, [scope, range, selectedTeam, selectedEmail, excludedTeams, excludedNicknames]);

  const timeseriesParams = useMemo(
    () => ({ ...queryParams, granularity }),
    [queryParams, granularity],
  );

  const summaryQuery = useCcUsageSummary(queryParams);
  const timeseriesQuery = useCcUsageTimeseries(timeseriesParams);
  const byUserQuery = useCcUsageByUser(queryParams);
  const byRepoQuery = useCcUsageByRepository(queryParams);
  const byTeamQuery = useCcUsageByTeam(queryParams);

  // 実行イベントセクションは全体集計のみ (cc-events API はスコープ・チーム除外フィルタ非対応)。
  // 期間と集計粒度はページ上部のセレクタと共有する。cortex リポジトリ / slack-triage skill の
  // トグルはこのセクション専用のローカル状態で持ち、既定は両方 OFF (= 除外)。
  const [eventType, setEventType] = useState<CcEventsEventType>('tool');
  const [includeCortex, setIncludeCortex] = useState(false);
  const [includeSlackTriage, setIncludeSlackTriage] = useState(false);
  const ccEventsParams = useMemo(
    () => ({ range, includeCortex, includeSlackTriage }),
    [range, includeCortex, includeSlackTriage],
  );
  const ccEventsSummaryQuery = useCcEventsSummary(ccEventsParams);
  const ccEventsTimeseriesQuery = useCcEventsTimeseries({
    ...ccEventsParams,
    granularity,
  });
  const ccEventsByNameQuery = useCcEventsByName({ ...ccEventsParams, eventType });
  const ccEventsByUserQuery = useCcEventsByUser(ccEventsParams);
  const ccEventsByProjectQuery = useCcEventsByProject(ccEventsParams);

  const stackedParams = useMemo(() => {
    if (scope === 'member') return undefined;
    const exclude = { excludeTeams: excludedTeams, excludeNicknames: excludedNicknames };
    if (scope === 'team') {
      return {
        scope,
        range,
        team: selectedTeam ?? undefined,
        groupBy: 'nickname' as const,
        granularity,
        ...exclude,
      };
    }
    return {
      scope: 'overall' as const,
      range,
      groupBy: 'team' as const,
      granularity,
      ...exclude,
    };
  }, [scope, range, selectedTeam, granularity, excludedTeams, excludedNicknames]);

  const stackedQuery = useCcUsageTimeseriesByGroup(
    stackedParams ?? {
      scope: 'member',
      email: undefined,
      range,
      groupBy: 'team',
      granularity,
    },
  );
  const stackedData = stackedParams ? stackedQuery.data : undefined;
  const isStackedLoading = stackedParams ? stackedQuery.isLoading : false;

  const emptyMessage =
    scope === 'team' && !selectedTeam
      ? t('ccUsage.emptyStates.selectTeam')
      : scope === 'member' && !selectedEmail
        ? t('ccUsage.emptyStates.selectMember')
        : null;

  const rankings = useMemo(() => {
    const userRanking = {
      title: t('ccUsage.breakdown.byUser'),
      emptyLabel: t('ccUsage.empty'),
      rows: (byUserQuery.data?.data ?? []).map((row) => ({
        key: row.nickname,
        primary: row.nickname,
        secondary: row.teamName ?? t('ccUsage.breakdown.unassigned'),
        tokens: tokenAmount(row, metric),
        secondaryMetric: row.messageCount,
        secondaryMetricLabel: t('ccUsage.metrics.messageCount'),
      })),
    };
    const repoRanking = {
      title: t('ccUsage.breakdown.byRepository'),
      emptyLabel: t('ccUsage.empty'),
      rows: (byRepoQuery.data?.data ?? []).map((row) => ({
        key: row.repository ?? '__unknown__',
        primary: row.repository
          ? formatRepositoryName(row.repository)
          : t('ccUsage.breakdown.unknownRepository'),
        secondary: `${row.uniqueUsers.toLocaleString()} ${t('ccUsage.breakdown.uniqueUsers')}`,
        tokens: tokenAmount(row, metric),
        secondaryMetric: row.messageCount,
        secondaryMetricLabel: t('ccUsage.metrics.messageCount'),
      })),
    };

    if (scope === 'overall') {
      const teamRanking = {
        title: t('ccUsage.breakdown.byTeam'),
        emptyLabel: t('ccUsage.empty'),
        rows: (byTeamQuery.data?.data ?? []).map((team) => ({
          key: team.teamName,
          primary: team.teamName,
          secondary: `${team.memberCount.toLocaleString()} ${t('ccUsage.breakdown.uniqueUsers')}`,
          tokens: tokenAmount(team, metric),
        })),
      };
      return [teamRanking, userRanking, repoRanking];
    }

    if (scope === 'team') {
      return [userRanking, repoRanking];
    }

    return [repoRanking];
  }, [scope, metric, byTeamQuery.data, byUserQuery.data, byRepoQuery.data, t]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">
          {t('ccUsage.title')}
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">{t('ccUsage.description')}</p>
      </header>
      <Card padding="none">
        <CardContent className="flex flex-wrap items-end gap-3 !mt-0 p-3">
          <div className="min-w-[160px] flex-1">
            <Picker
              label={t('ccUsage.scopeSelectLabel')}
              value={scope}
              options={scopeOptions.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => {
                if (v) updateSearch({ scope: v as CcUsageSearch['scope'] });
              }}
            />
          </div>
          {scope === 'team' ? (
            <div className="min-w-[200px] flex-1">
              <Picker
                label={t('ccUsage.teamSelectLabel')}
                value={selectedTeam ?? null}
                options={teamOptions.map((o) => ({ value: o.value, label: o.label }))}
                onChange={(v) => {
                  updateSearch({ team: v ?? undefined });
                }}
                placeholder={t('ccUsage.placeholder.team')}
                searchable
              />
            </div>
          ) : null}
          {scope === 'member' ? (
            <div className="min-w-[240px] flex-1 flex flex-col gap-1">
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('ccUsage.memberSelectLabel')}
              </span>
              <MemberPicker
                aria-label={t('ccUsage.memberSelectLabel')}
                value={selectedEmail ?? null}
                onChange={(value) => {
                  updateSearch({ email: value ?? undefined });
                }}
                placeholder={t('ccUsage.placeholder.member')}
                searchPlaceholder={t('ccUsage.memberSearchPlaceholder')}
                emptyMessage={t('ccUsage.memberEmpty')}
              />
            </div>
          ) : null}
          <div className="min-w-[160px] flex-1">
            <Picker
              label={t('ccUsage.periodSelectLabel')}
              value={range}
              options={periodOptions.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => {
                if (v) updateSearch({ range: v as CcUsageSearch['range'] });
              }}
            />
          </div>
          <div className="min-w-[160px] flex-1">
            <Picker
              label={t('ccUsage.granularitySelectLabel')}
              value={granularity}
              options={granularityOptions.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => {
                if (v) updateSearch({ granularity: v as CcUsageSearch['granularity'] });
              }}
            />
          </div>
          <div className="min-w-[200px] flex-1">
            <Picker
              label={t('ccUsage.tokenMetricSelectLabel')}
              value={metric}
              options={metricOptions.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(v) => {
                if (v) updateSearch({ metric: v as CcUsageSearch['metric'] });
              }}
            />
          </div>
          <div className="min-w-[220px] flex-1">
            <MultiPicker
              label={t('ccUsage.excludeTeamsLabel')}
              value={excludedTeams}
              options={teamOptions}
              onChange={(v) => {
                updateSearch({ excludeTeams: v.length > 0 ? v.join(',') : undefined });
              }}
              placeholder={t('ccUsage.excludePlaceholder')}
              searchPlaceholder={t('ccUsage.excludeTeamsSearchPlaceholder')}
              emptyMessage={t('ccUsage.empty')}
            />
          </div>
          <div className="min-w-[220px] flex-1">
            <MultiPicker
              label={t('ccUsage.excludeMembersLabel')}
              value={excludedNicknames}
              options={memberExcludeOptions}
              onChange={(v) => {
                updateSearch({ excludeNicknames: v.length > 0 ? v.join(',') : undefined });
              }}
              placeholder={t('ccUsage.excludePlaceholder')}
              searchPlaceholder={t('ccUsage.excludeMembersSearchPlaceholder')}
              emptyMessage={t('ccUsage.empty')}
            />
          </div>
        </CardContent>
      </Card>
      <CcUsagePanel
        summary={summaryQuery.data}
        isSummaryLoading={summaryQuery.isLoading}
        timeseries={timeseriesQuery.data}
        timeseriesByGroup={stackedData}
        isTimeseriesByGroupLoading={isStackedLoading}
        emptyMessage={emptyMessage}
        metric={metric}
      />
      {!emptyMessage ? <CcUsageRankingsPanel rankings={rankings} /> : null}
      <section className="space-y-3">
        <header className="space-y-1">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {t('ccEvents.title')}
          </h2>
          <p className="text-sm text-[var(--color-text-muted)]">{t('ccEvents.description')}</p>
        </header>
        <CcEventsPanel
          summary={ccEventsSummaryQuery.data}
          isSummaryLoading={ccEventsSummaryQuery.isLoading}
          timeseries={ccEventsTimeseriesQuery.data}
          byName={ccEventsByNameQuery.data}
          byUser={ccEventsByUserQuery.data}
          byProject={ccEventsByProjectQuery.data}
          eventType={eventType}
          onEventTypeChange={setEventType}
          includeCortex={includeCortex}
          onIncludeCortexChange={setIncludeCortex}
          includeSlackTriage={includeSlackTriage}
          onIncludeSlackTriageChange={setIncludeSlackTriage}
        />
      </section>
    </div>
  );
}
